import React, { useState, useEffect } from 'react';
import { useAuth, QUICK_LOGIN_PROFILES } from '../../context/AuthContext';
import { useToast } from './Toast';
import { Role } from '../../types';
import { Settings, X, Check, ShieldCheck, UserCheck, ChevronRight } from 'lucide-react';
import { isSuperAdmin, getHighestRole } from '../../utils/formatters';

export const RoleSwitcherFloating: React.FC = () => {
  const { currentUser, loginAs } = useAuth();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Lock body scroll when modal drawer is open (per project guidelines & rules)
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const rolesList: string[] = [
    'Superadmin',
    'Pengerusi',
    'Timbalan Pengerusi',
    'Setiausaha',
    'Pen. Setiausaha',
    'Bendahari',
    'Ketua Gelanggang',
    'AJK',
    'Ahli',
    'Ahli Seumur Hidup',
    'Waris',
  ];

  const handleSelectRole = (roleKey: string) => {
    loginAs(roleKey);
    const profile = QUICK_LOGIN_PROFILES[roleKey];
    if (profile) {
      showToast('success', 'Peranan Ditukar', `Kini menguji sebagai ${profile.name} (${roleKey === 'Superadmin' ? 'Superadmin' : profile.role})`);
    }
    setIsOpen(false);
  };

  if (!isLocalhost) {
    return null;
  }

  return (
    <>
      {/* Floating Gear Icon Button on Right Side */}
      <div className="fixed right-3 sm:right-0 bottom-20 sm:top-1/2 sm:-translate-y-1/2 z-40 no-print">
        <button
          onClick={() => setIsOpen(true)}
          className="group p-2.5 sm:p-3 bg-slate-900/95 hover:bg-slate-900 text-amber-400 rounded-full sm:rounded-none sm:rounded-l-2xl shadow-2xl border border-amber-400/30 sm:border-l-4 sm:border-asmm-red sm:border-y sm:border-r-0 border-slate-700/60 transition-all transform hover:scale-105 sm:hover:-translate-x-1 backdrop-blur-md cursor-pointer focus:outline-none flex items-center justify-center active:scale-95"
          title={`Uji Peranan (${currentUser?.role || 'Guest'})`}
          aria-label="Uji Peranan"
        >
          <div className="relative">
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400 group-hover:rotate-90 transition-transform duration-500 shrink-0" />
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
            </span>
          </div>
        </button>
      </div>

      {/* Expanded Side Drawer Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate-fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer Content */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl z-10 flex flex-col justify-between overflow-hidden border-l border-slate-200 animate-slide-left">
            {/* Header */}
            <div className="bg-slate-900 text-white p-5 border-b border-slate-800 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-asmm-red/20 border border-asmm-red/40 rounded-xl text-amber-400 shrink-0">
                  <Settings className="w-6 h-6 animate-spin-slow" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-extrabold text-base text-white tracking-tight">
                      Mod Ujian Peranan
                    </h3>
                    <span className="px-2 py-0.5 text-[10px] font-extrabold bg-amber-400/20 text-amber-300 border border-amber-400/30 rounded-full">
                      Testing
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    Pilih peranan di bawah untuk menguji akses &amp; paparan sistem.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors shrink-0"
                aria-label="Tutup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Current Active User Banner */}
            {currentUser && (
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={currentUser.photoUrl || '/default-avatar.svg'}
                    alt={currentUser.name}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                    className="w-10 h-10 rounded-full object-cover border-2 border-asmm-red shadow-sm shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                      Pengguna Semasa
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 truncate">
                      {currentUser.name}
                    </div>
                    <div className="text-[11px] text-slate-600 font-medium truncate">
                      {currentUser.bengkung}
                    </div>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-lg text-xs font-extrabold bg-asmm-red text-white shadow-sm shrink-0">
                  {getHighestRole(currentUser)}
                </span>
              </div>
            )}

            {/* Role Options List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2.5 bg-slate-50/50">
              <div className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider px-1 flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5 text-asmm-red" />
                <span>Senarai Peranan Untuk Diuji:</span>
              </div>

              {rolesList.map((r) => {
                const profile = QUICK_LOGIN_PROFILES[r];
                if (!profile) return null;
                const isActive = r === 'Superadmin'
                  ? isSuperAdmin(currentUser) && currentUser?.id === profile.id
                  : currentUser?.id === profile.id;

                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => handleSelectRole(r)}
                    className={`w-full text-left p-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3 group shadow-sm ${
                      isActive
                        ? 'bg-red-50/90 border-asmm-red ring-1 ring-asmm-red/30'
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={profile.photoUrl || '/default-avatar.svg'}
                        alt={profile.name}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
                        className={`w-10 h-10 rounded-full object-cover border shadow-xs shrink-0 ${
                          isActive ? 'border-asmm-red' : 'border-slate-200'
                        }`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-xs font-extrabold ${
                              isActive ? 'text-asmm-red' : 'text-slate-900 group-hover:text-asmm-red'
                            }`}
                          >
                            {r === 'Superadmin' 
                              ? 'Superadmin (Hafiz)' 
                              : r === 'Ahli' 
                                ? 'Ahli Biasa (Shukor)' 
                                : r}
                          </span>
                          {r === 'Superadmin' && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 text-[9px] font-extrabold rounded-md uppercase tracking-wider">
                              Akses Penuh
                            </span>
                          )}
                          {r === 'Ahli' && (
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-200 text-[9px] font-bold rounded-md uppercase tracking-wider">
                              Ahli Standard
                            </span>
                          )}
                          {isActive && (
                            <span className="px-1.5 py-0.5 bg-asmm-red text-white text-[9px] font-bold rounded">
                              Aktif
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-semibold text-slate-700 truncate">
                          {profile.name}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {profile.bengkung || profile.role}
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0">
                      {isActive ? (
                        <div className="w-6 h-6 rounded-full bg-asmm-red text-white flex items-center justify-center shadow-xs">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-slate-100 group-hover:bg-asmm-red group-hover:text-white text-slate-400 flex items-center justify-center transition-colors">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer note */}
            <div className="p-4 bg-white border-t border-slate-200 text-center text-[11px] text-slate-500 font-medium">
              <div className="flex items-center justify-center gap-1 text-slate-600 mb-0.5">
                <ShieldCheck className="w-3.5 h-3.5 text-asmm-red" />
                <span className="font-bold">Simulasi Akses Peranan</span>
              </div>
              Tukar peranan serta-merta untuk menguji kebenaran modul.
            </div>
          </div>
        </div>
      )}
    </>
  );
};
